/**
 * Records Organizace / Obchody / Nakupovat navigations onto the browse visit stack.
 * Mount once under CustomerAppShell (alongside hierarchical system-back).
 *
 * Records in useLayoutEffect so fixed Zpět (same component) re-reads the stack
 * before paint when a push happens.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  browseVisitEntriesEqual,
  consumeBrowseVisitSkipRecord,
  isBrowseVisitStackablePath,
  normalizeBrowseVisitEntry,
  pushBrowseVisit,
  type BrowseVisitEntry,
} from '../navigation/browseVisitStack.js';
import { isEphemeralBackRestorePath } from '../navigation/ephemeralBackRestore.js';

/**
 * On PUSH/REPLACE, remember stackable browse paths we leave — including when
 * entering checkout/payment — so Zpět still works after the journey ends.
 * Never push ephemeral payment surfaces. Stack-driven ← sets skip so we do not
 * re-push the left page. POP is left to hierarchical system-back.
 */
export function useBrowseVisitRecorder(): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRef = useRef<BrowseVisitEntry | null>(null);
  const [, setStackEpoch] = useState(0);

  useLayoutEffect(() => {
    const current = normalizeBrowseVisitEntry(location.pathname, location.search);

    if (previousRef.current === null) {
      previousRef.current = current;
      return;
    }

    if (browseVisitEntriesEqual(previousRef.current, current)) {
      return;
    }

    const previous = previousRef.current;

    if (consumeBrowseVisitSkipRecord()) {
      previousRef.current = current;
      return;
    }

    if (navigationType === 'POP') {
      previousRef.current = current;
      return;
    }

    let pushed = false;
    // Remember browse screens even when navigating into payment monitoring.
    if (
      isBrowseVisitStackablePath(previous.pathname) &&
      !isEphemeralBackRestorePath(previous.pathname)
    ) {
      pushBrowseVisit(previous);
      pushed = true;
    }

    previousRef.current = current;
    if (pushed) {
      // Sync re-render before paint so useShellDepthBack peeks the new stack.
      setStackEpoch((n) => n + 1);
    }
  }, [location.pathname, location.search, navigationType]);
}
